import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from './authService';

// API Functions
const userImageApi = {
  // Get all user images
  getUserImages: async (params = {}) => {
    const response = await api.get('/user/images', { params });
    return response.data;
  },

  // Upload a new image
  uploadImage: async (formData, onUploadProgress) => {
    const config = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    };
    
    // Add progress tracking if callback provided
    if (onUploadProgress) {
      config.onUploadProgress = (progressEvent) => {
        if (progressEvent.total) {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          onUploadProgress(percentCompleted);
        }
      };
    }
    
    const response = await api.post('/user/images', formData, config);
    return response.data;
  },

  // Update an image
  updateImage: async ({ id, data }) => {
    const response = await api.put(`/user/images/${id}`, data);
    return response.data;
  },

  // Delete an image
  deleteImage: async (id) => {
    const response = await api.delete(`/user/images/${id}`);
    return response.data;
  },

  // Set image as default
  setDefaultImage: async (id) => {
    const response = await api.put(`/user/images/${id}/set-default`);
    return response.data;
  },

  // Reorder images
  reorderImages: async (imageIds) => {
    const response = await api.put('/user/images/reorder', { image_ids: imageIds });
    return response.data;
  },
};

// React Query Hooks

// Get user images
export const useUserImages = (params = {}, options = {}) => {
  return useQuery({
    queryKey: ['user-images', params],
    queryFn: () => userImageApi.getUserImages(params),
    ...options
  });
};

// Upload image mutation
export const useUploadImage = (options = {}) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (variables) => {
      const { formData, onUploadProgress } = variables;
      return userImageApi.uploadImage(formData, onUploadProgress);
    },
    onSuccess: (data, variables) => {
      // Invalidate and refetch images immediately
      queryClient.invalidateQueries({ queryKey: ['user-images'] });
      queryClient.refetchQueries({ queryKey: ['user-images'] });
      queryClient.invalidateQueries({ queryKey: ['vendor-subscription-status'] });
    },
    ...options
  });
};

// Update image mutation
export const useUpdateImage = (options = {}) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: userImageApi.updateImage,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-images'] });
    },
    ...options
  });
};

// Delete image mutation
export const useDeleteImage = (options = {}) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: userImageApi.deleteImage,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-images'] });
      queryClient.invalidateQueries({ queryKey: ['vendor-subscription-status'] });
    },
    ...options
  });
};

// Set default image mutation
export const useSetDefaultImage = (options = {}) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: userImageApi.setDefaultImage,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-images'] });
    },
    ...options
  });
};

// Reorder images mutation
export const useReorderImages = (options = {}) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: userImageApi.reorderImages,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-images'] });
    },
    ...options
  });
};

export default userImageApi;

